// src/interfaces/sharepointSearchTypes.ts

/**
 * SharePoint Search Tool Type Definitions
 *
 * These types match the backend's sharepoint_search tool output.
 * The tool supports two actions: "search" (find documents) and "read" (download + extract content).
 *
 * Backend response structure (via tool_use_result chunk):
 * {
 *   type: "tool_result",
 *   tool_name: "sharepoint_search",
 *   tool_use_id: string,
 *   content: { action, status, ... }
 * }
 */

// --- Backend response types (snake_case, match backend JSON exactly) ---

/** A single file result from a SharePoint search action */
export interface SharePointSearchFile {
  name: string;
  summary: string;
  web_url: string;
  size: number;
  mime_type: string;
  last_modified: string;
  created: string;
  drive_id: string;
  item_id: string;
}

/** Backend response for action: "search" */
export interface SharePointSearchResponse {
  status: "success";
  action: "search";
  original_query: string;
  total_results: number;
  showing: number;
  more_results_available: boolean;
  files: SharePointSearchFile[];
}

/** Backend response for action: "read" */
export interface SharePointReadResponse {
  status: "success";
  action: "read";
  file_name: string;
  file_size_bytes: number;
  mime_type: string;
  web_url: string;
  total_content_length: number;
  offset: number;
  next_offset: number | null;
  truncated: boolean;
  chars_returned: number;
  content: string;
  drive_id: string;
  item_id: string;
}

/** Backend error response (any action) */
export interface SharePointErrorResponse {
  status: "error";
  action: "search" | "read";
  error_code: string;
  message: string;
}

// --- Normalized UI types (camelCase, for React components) ---

export interface NormalizedSharePointSearch {
  status: "success";
  action: "search";
  originalQuery: string;
  totalResults: number;
  showing: number;
  moreResultsAvailable: boolean;
  files: SharePointSearchFile[];
  toolUseId?: string;
  toolInput?: SharePointToolInput;
}

export interface NormalizedSharePointRead {
  status: "success";
  action: "read";
  fileName: string;
  fileSizeBytes: number;
  mimeType: string;
  webUrl: string;
  totalContentLength: number;
  offset: number;
  nextOffset: number | null;
  truncated: boolean;
  charsReturned: number;
  content: string;
  driveId: string;
  itemId: string;
  toolUseId?: string;
  toolInput?: SharePointToolInput;
}

export interface NormalizedSharePointError {
  status: "error";
  action: "search" | "read";
  errorCode: string;
  errorMessage: string;
  toolUseId?: string;
  toolInput?: SharePointToolInput;
}

/** Discriminated union on action + status */
export type NormalizedSharePointResult =
  | NormalizedSharePointSearch
  | NormalizedSharePointRead
  | NormalizedSharePointError;

// --- Tool input type (captured from input_json_delta) ---

export interface SharePointToolInput {
  action: "search" | "read";
  query?: string;
  driveId?: string;
  itemId?: string;
}

// --- Raw backend response (top-level chunk.text in tool_use_result) ---

export interface SharePointSearchResult {
  type: "tool_result";
  tool_name: "sharepoint_search";
  tool_use_id: string;
  content: SharePointSearchResponse | SharePointReadResponse | SharePointErrorResponse;
}

// --- Type guard ---

/**
 * Type guard to check if an object is a SharePointSearchResult.
 * Detects by `tool_name === "sharepoint_search"` and checks for `content` wrapper.
 */
export function isSharePointSearchResult(obj: unknown): obj is SharePointSearchResult {
  if (!obj || typeof obj !== "object") return false;

  const candidate = obj as Record<string, unknown>;
  if (candidate.tool_name !== "sharepoint_search") return false;

  const content = candidate.content;
  if (!content || typeof content !== "object") return false;

  const inner = content as Record<string, unknown>;

  // Must have action and status fields
  if (typeof inner.action !== "string" || typeof inner.status !== "string") return false;

  return true;
}

// --- Normalizer ---

/**
 * Normalizes a raw SharePoint search result into a flat UI-friendly structure.
 * Returns null if the input doesn't pass the type guard.
 */
export function normalizeSharePointResult(obj: unknown): NormalizedSharePointResult | null {
  if (!isSharePointSearchResult(obj)) return null;

  const content = obj.content;

  if (content.status === "error") {
    const errorContent = content as SharePointErrorResponse;
    return {
      action: errorContent.action,
      status: "error",
      errorCode: errorContent.error_code,
      errorMessage: errorContent.message || "SharePoint operation failed",
      toolUseId: obj.tool_use_id,
    };
  }

  if (content.action === "search" && content.status === "success") {
    const searchContent = content as SharePointSearchResponse;
    return {
      action: "search",
      status: "success",
      originalQuery: searchContent.original_query,
      totalResults: searchContent.total_results,
      showing: searchContent.showing,
      moreResultsAvailable: searchContent.more_results_available,
      files: searchContent.files,
      toolUseId: obj.tool_use_id,
    };
  }

  if (content.action === "read" && content.status === "success") {
    const readContent = content as SharePointReadResponse;
    return {
      action: "read",
      status: "success",
      fileName: readContent.file_name,
      fileSizeBytes: readContent.file_size_bytes,
      mimeType: readContent.mime_type,
      webUrl: readContent.web_url,
      totalContentLength: readContent.total_content_length,
      offset: readContent.offset,
      nextOffset: readContent.next_offset,
      truncated: readContent.truncated,
      charsReturned: readContent.chars_returned,
      content: readContent.content,
      driveId: readContent.drive_id,
      itemId: readContent.item_id,
      toolUseId: obj.tool_use_id,
    };
  }

  // Unknown action/status combo — return error
  const inner = content as Record<string, unknown>;
  return {
    action: inner.action === "read" ? "read" : "search",
    status: "error",
    errorCode: "UNKNOWN_RESPONSE",
    errorMessage: `Unexpected response: action=${String(inner.action)}, status=${String(inner.status)}`,
    toolUseId: obj.tool_use_id,
  };
}

// --- Input parser ---

/**
 * Parses a SharePoint tool input from accumulated input_json_delta data.
 * Extracts action + context fields from the JSON payload.
 */
export function parseSharePointToolInput(
  json: Record<string, unknown>
): SharePointToolInput | null {
  const action = json.action;
  if (action !== "search" && action !== "read") return null;

  return {
    action,
    query: typeof json.query === "string" ? json.query : undefined,
    driveId: typeof json.drive_id === "string" ? json.drive_id : undefined,
    itemId: typeof json.item_id === "string" ? json.item_id : undefined,
  };
}
